/**
 * BracketTooltipContent -- the shown bracket's explanation, as the backend computed it:
 * the owner's declaration (and the B1 rules that block it), the rule floor and its reasons,
 * the "plays like" annotations, the power estimate (a position, never a placement) and the
 * B4+ combo warning. It renders; it never computes a bracket (single source of truth:
 * metacommand/deck/bracket_floor.py and plays_like.py).
 */

import { BracketReadCallouts, FloorReasons, PlaysLikeReasons } from '@/components/FloorReasons';
import { BRACKET_COLORS, BRACKET_LABELS } from '@/lib/constants';
import type { BracketPosition, PowerLevelResult } from '@/lib/api';

/** "B3 · high" — the shown bracket and the deck's position within it. */
export function bracketPositionText(bracket: number, position: BracketPosition | null | undefined): string {
  return position ? `B${bracket} · ${position}` : `B${bracket}`;
}

/** The power estimate as the tooltip states it: the unrounded score when the analysis stored one. */
export function powerEstimateText(powerLevel?: PowerLevelResult | null): string | null {
  const raw = powerLevel?.power_score_raw;
  if (typeof raw === 'number') return `${raw.toFixed(1)}/10`;
  const scale = powerLevel?.traditional_scale;
  return typeof scale === 'number' ? `${scale}/10` : null;
}

export function BracketTooltipContent({
  bracket,
  powerLevel,
}: {
  bracket: number;
  powerLevel?: PowerLevelResult | null;
}) {
  const minBracket = powerLevel?.minimum_bracket ?? bracket;
  const reasons = powerLevel?.floor_reasons ?? [];
  const playsLike = powerLevel?.plays_like_reasons ?? [];
  const declaration = powerLevel?.bracket_declaration ?? null;
  const position = powerLevel?.bracket_position ?? null;
  const comboWarning = powerLevel?.bracket_combo_warning ?? null;
  const callouts = powerLevel?.bracket_read_callouts ?? [];
  const estimate = powerEstimateText(powerLevel);

  const minColor = BRACKET_COLORS[minBracket] ?? 'var(--outline-variant)';
  const shownColor = BRACKET_COLORS[bracket] ?? 'var(--outline-variant)';
  const bracketLabel = BRACKET_LABELS[bracket] ?? `B${bracket}`;
  const minLabel = BRACKET_LABELS[minBracket] ?? `B${minBracket}`;

  return (
    <div className="flex flex-col gap-2">
      {/* Header — the shown bracket and the position within it */}
      <div className="font-semibold" style={{ color: shownColor }}>
        Bracket {bracket} — {bracketLabel}{position ? ` · ${position}` : ''}
      </div>

      {/* The owner's declaration: B1 / B5 come only from here */}
      {declaration && (
        <div className="flex flex-col gap-1 text-on-surface-variant" data-testid="bracket-declaration">
          <span>{declaration.text}</span>
          {!declaration.applied && declaration.blocking.length > 0 && <FloorReasons reasons={declaration.blocking} />}
        </div>
      )}

      {/* The floor and the hard-rule reasons behind it */}
      {reasons.length > 0 && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-on-surface-variant">
            <span
              className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold text-white shrink-0"
              style={{ background: minColor }}
            >
              {minBracket}
            </span>
            <span className="font-medium text-on-surface-variant">
              {bracket !== minBracket ? `Rule floor: B${minBracket} (${minLabel})` : 'What places this here:'}
            </span>
          </div>
          <FloorReasons reasons={reasons} />
        </div>
      )}

      {/* Plays like — annotations that never move the shown bracket */}
      {(playsLike.length > 0 || callouts.length > 0) && (
        <div className="pt-1 border-t border-outline-variant flex flex-col gap-1">
          <span className="font-medium text-on-surface-variant">Plays like</span>
          {playsLike.length > 0 && <PlaysLikeReasons reasons={playsLike} />}
          {callouts.length > 0 && <BracketReadCallouts callouts={callouts} />}
        </div>
      )}

      {/* The x/10: a position within the bracket, never a placement */}
      {estimate && (
        <div className="pt-1 border-t border-outline-variant text-on-surface-variant">
          Power estimate {estimate} — a rough guess; it places the deck within its bracket
          {position ? ` (${position})` : ''}, never in one
        </div>
      )}

      {/* B4+ combo warning — the combos are why the rule floor is B4, or they outrank a lower deck */}
      {comboWarning && (
        <div className="pt-1 border-t border-outline-variant flex gap-1.5">
          <span className="material-icons text-sm shrink-0 mt-0.5" style={{ color: 'var(--warning-text, #fdd663)' }}>
            hub
          </span>
          <span className="text-on-surface-variant">{comboWarning.practical_warning}</span>
        </div>
      )}
    </div>
  );
}
